import {
  ANAESTHETIC_PRIORITY_OPTIONS,
  ANAESTHETIC_SETTING_OPTIONS,
  COMMUNICATION_CATEGORY_OPTIONS,
  EDT_ACTIVITY_OPTIONS,
  dayPart,
  isWeekend,
  labelFor,
  procedureLabel,
  timeOfDay,
} from '@iculogbook/shared';
import type {
  AgeBand,
  CapabilityLevel,
  EdtActivity,
  LocalAnaestheticCase,
  LocalAnyEntry,
  LocalCase,
  LocalCommunication,
  LocalEdtEntry,
  LocalProcedure,
  TimeOfDay,
} from '@iculogbook/shared';

/**
 * The entry list's data model: how stored records become the grouped,
 * searchable list the Entries tab renders. Pure functions over arrays, so the
 * behaviour that matters can be tested without a browser.
 *
 * The shape the screen needs is not the shape the database holds. A case and
 * the procedures inside it are separate rows in Dexie and one clinical event on
 * screen, and this is where that translation happens — once, here, rather than
 * in a component that also has scrolling to worry about.
 */

/** The five kinds a row can be. Only a Case has children. */
export type EntryKind = 'case' | 'procedure' | 'edt' | 'anaesthetic' | 'communication';

export type EntryItem =
  | { kind: 'case'; record: LocalCase; procedures: readonly LocalProcedure[] }
  | { kind: 'procedure'; record: LocalProcedure }
  | { kind: 'edt'; record: LocalEdtEntry }
  | { kind: 'anaesthetic'; record: LocalAnaestheticCase }
  | { kind: 'communication'; record: LocalCommunication };

/**
 * Everything the list draws from.
 *
 * An object rather than one positional array per kind: this is the second kind
 * added since the signature was written, and a fifth positional array is how a
 * caller ends up passing procedures where EDT entries were expected.
 */
export type EntrySources = {
  cases: readonly LocalCase[];
  procedures: readonly LocalProcedure[];
  edtEntries?: readonly LocalEdtEntry[];
  anaestheticCases?: readonly LocalAnaestheticCase[];
  communications?: readonly LocalCommunication[];
};

export type DayGroup = {
  /** `YYYY-MM-DD` in local time — the day the trainee worked, not UTC's day. */
  key: string;
  date: Date;
  items: readonly EntryItem[];
};

export type SyncState = 'synced' | 'pending' | 'failed';

export type EntryFilters = {
  query: string;
  from: Date | null;
  to: Date | null;
  /** Empty means no restriction, for every list here. */
  kinds: readonly EntryKind[];
  procedureKeys: readonly string[];
  activities: readonly EdtActivity[];
  capabilityLevels: readonly CapabilityLevel[];
  ageBands: readonly AgeBand[];
  timesOfDay: readonly TimeOfDay[];
  weekendOnly: boolean;
};

export const NO_FILTERS: EntryFilters = {
  query: '',
  from: null,
  to: null,
  kinds: [],
  procedureKeys: [],
  activities: [],
  capabilityLevels: [],
  ageBands: [],
  timesOfDay: [],
  weekendOnly: false,
};

/** What the "3 filters" badge counts. The search box is shown separately. */
export function activeFilterCount(filters: EntryFilters): number {
  return [
    filters.from !== null || filters.to !== null,
    filters.kinds.length > 0,
    filters.procedureKeys.length > 0,
    filters.activities.length > 0,
    filters.capabilityLevels.length > 0,
    filters.ageBands.length > 0,
    filters.timesOfDay.length > 0,
    filters.weekendOnly,
  ].filter(Boolean).length;
}

function normalise(value: string): string {
  return value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/**
 * The text a search looks at: the fields DATA_MODEL.md lists as free text, plus
 * the procedure's label, because nobody searches for `arterial_line`.
 */
function haystack(entry: LocalAnyEntry): string {
  const parts: string[] = [];

  // Covers a case's title and an EDT entry's, which are the same field to a
  // reader typing into a search box.
  if ('title' in entry) parts.push(entry.title);
  if ('procedureKey' in entry) {
    parts.push(procedureLabel(entry.procedureKey));
    if (entry.variant !== undefined) parts.push(entry.variant);
    if (entry.complications !== undefined) parts.push(entry.complications);
    if (entry.indication !== undefined) parts.push(entry.indication);
  }
  // Nobody searches for `teaching_delivered`.
  if ('activity' in entry) parts.push(labelFor(EDT_ACTIVITY_OPTIONS, entry.activity));
  // Nobody searches for `withdrawal_of_treatment` either, and a Communication
  // has no title of its own for the category to stand in for.
  if ('category' in entry) parts.push(labelFor(COMMUNICATION_CATEGORY_OPTIONS, entry.category));
  if ('setting' in entry) {
    parts.push(labelFor(ANAESTHETIC_SETTING_OPTIONS, entry.setting));
    if (entry.priority !== undefined) {
      parts.push(labelFor(ANAESTHETIC_PRIORITY_OPTIONS, entry.priority));
    }
  }
  if (entry.note !== undefined) parts.push(entry.note);

  return normalise(parts.join(' '));
}

export function matchesQuery(entry: LocalAnyEntry, query: string): boolean {
  const needle = normalise(query.trim());
  if (needle === '') return true;

  return haystack(entry).includes(needle);
}

/** Everything except the text search, which the three kinds apply differently. */
function matchesFacets(entry: LocalAnyEntry, filters: EntryFilters): boolean {
  const when = entry.occurredAt;

  if (filters.from !== null && when.getTime() < filters.from.getTime()) return false;
  if (filters.to !== null && when.getTime() > filters.to.getTime()) return false;
  if (filters.timesOfDay.length > 0 && !filters.timesOfDay.includes(timeOfDay(when))) return false;
  if (filters.weekendOnly && !isWeekend(when)) return false;

  /*
   * Capability level and age band describe a patient, and an EDT entry has
   * neither. Filtering on one therefore excludes EDT entries rather than
   * exempting them: someone asking for "level 3, ages 71–85" is asking about
   * clinical work, and an afternoon of reading is not an answer to that.
   */
  if (filters.capabilityLevels.length > 0) {
    if (!('capabilityLevel' in entry)) return false;
    if (!filters.capabilityLevels.includes(entry.capabilityLevel)) return false;
  }
  if (filters.ageBands.length > 0) {
    if (!('ageBand' in entry)) return false;
    if (!filters.ageBands.includes(entry.ageBand)) return false;
  }

  if (filters.procedureKeys.length > 0) {
    if (!('procedureKey' in entry)) return false;
    if (!filters.procedureKeys.includes(entry.procedureKey)) return false;
  }

  if (filters.activities.length > 0) {
    if (!('activity' in entry)) return false;
    if (!filters.activities.includes(entry.activity)) return false;
  }

  return true;
}

function matchesKind(kind: EntryKind, filters: EntryFilters): boolean {
  return filters.kinds.length === 0 || filters.kinds.includes(kind);
}

/** `YYYY-MM-DD` in the browser's zone, which is where the work happened. */
export function dayKey(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${String(date.getFullYear())}-${month}-${day}`;
}

/**
 * Builds the list: newest first, grouped by day, with each case carrying the
 * procedures recorded inside it.
 *
 * A procedure whose case is missing or deleted is shown standalone rather than
 * hidden. Losing sight of a logged procedure because of a broken link would be
 * the worst kind of quiet failure — the trainee did the work and the app would
 * simply stop mentioning it.
 */
export function buildDayGroups(
  sources: EntrySources,
  filters: EntryFilters = NO_FILTERS,
): DayGroup[] {
  const { cases, procedures, edtEntries = [], anaestheticCases = [], communications = [] } =
    sources;
  const caseById = new Map(cases.map((record) => [record.id, record]));
  const childrenByCase = new Map<string, LocalProcedure[]>();
  const items: EntryItem[] = [];

  for (const procedure of procedures) {
    const parentId = procedure.caseId;
    const parent = parentId === undefined ? undefined : caseById.get(parentId);

    if (parent === undefined) {
      if (matchesKind('procedure', filters) && matchesFacets(procedure, filters)) {
        if (matchesQuery(procedure, filters.query))
          items.push({ kind: 'procedure', record: procedure });
      }
      continue;
    }

    const siblings = childrenByCase.get(parent.id) ?? [];
    siblings.push(procedure);
    childrenByCase.set(parent.id, siblings);
  }

  for (const record of cases) {
    if (!matchesKind('case', filters)) continue;

    const children = (childrenByCase.get(record.id) ?? [])
      .filter((child) => matchesFacets(child, filters))
      .sort((a, b) => a.occurredAt.getTime() - b.occurredAt.getTime());

    const caseMatches = matchesFacets(record, filters) && matchesQuery(record, filters.query);
    const matchingChildren = children.filter((child) => matchesQuery(child, filters.query));

    // The case is shown if it matches, or if something inside it does — a
    // search for "arterial line" should find the admission it happened in.
    if (caseMatches) {
      items.push({ kind: 'case', record, procedures: children });
    } else if (matchingChildren.length > 0 && matchesFacets(record, filters)) {
      items.push({ kind: 'case', record, procedures: matchingChildren });
    }
  }

  for (const record of edtEntries) {
    if (!matchesKind('edt', filters)) continue;
    if (!matchesFacets(record, filters)) continue;
    if (!matchesQuery(record, filters.query)) continue;

    items.push({ kind: 'edt', record });
  }

  for (const record of anaestheticCases) {
    if (!matchesKind('anaesthetic', filters)) continue;
    if (!matchesFacets(record, filters)) continue;
    if (!matchesQuery(record, filters.query)) continue;

    items.push({ kind: 'anaesthetic', record });
  }

  for (const record of communications) {
    if (!matchesKind('communication', filters)) continue;
    if (!matchesFacets(record, filters)) continue;
    if (!matchesQuery(record, filters.query)) continue;

    items.push({ kind: 'communication', record });
  }

  items.sort((a, b) => b.record.occurredAt.getTime() - a.record.occurredAt.getTime());

  const groups: DayGroup[] = [];
  let current: DayGroup | undefined;

  for (const item of items) {
    const key = dayKey(item.record.occurredAt);

    if (current === undefined || current.key !== key) {
      current = { key, date: item.record.occurredAt, items: [] };
      groups.push(current);
    }

    (current.items as EntryItem[]).push(item);
  }

  return groups;
}

/** How many entries a set of groups holds, counting nested procedures. */
export function countEntries(groups: readonly DayGroup[]): number {
  return groups.reduce(
    (total, group) =>
      total +
      group.items.reduce(
        (inner, item) => inner + (item.kind === 'case' ? 1 + item.procedures.length : 1),
        0,
      ),
    0,
  );
}

export function syncStateOf(entry: LocalAnyEntry, failedIds: ReadonlySet<string>): SyncState {
  if (failedIds.has(entry.id)) return 'failed';
  return entry.syncedAt === null ? 'pending' : 'synced';
}

/** For the row's secondary line: "Weekday night", "Weekend day". */
export function whenLabel(entry: LocalAnyEntry): string {
  return dayPart(entry.occurredAt);
}
